import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext/AuthContext'
import { useModal } from '../../../contexts/ModalContext/ModalContext'
import { Map, MapElement, MapSettings } from '../../../types'
import { TopToolbar } from '../TopToolbar/TopToolbar'
import { ToolsPanel, Tool } from '../ToolsPanel'
import { MapCanvas } from '../MapCanvas/MapCanvas'
import { MapCanvasHandle } from '../MapCanvas/types'
import { PropertiesPanel } from '../PropertiesPanel/PropertiesPanel'
import NavigatorPanel from '../NavigatorPanel'
import { SharePanel } from '../../share/SharePanel'
import { BrandingPanel } from '../BrandingPanel'
import EmbedPanel from '../EmbedPanel'
import { loadMapFromAPI, saveMapToAPI } from './helpers'
import {
  EditorContainer,
  MainLayout,
  LeftPanel,
  CenterPanel,
  RightPanel,
  LoadingContainer,
  MobileNotice,
  LeftToggleButton,
  RightToggleButton,
  PanelContent,
  PanelSwitcher,
  SwitchButton,
  PanelContentArea,
} from './styled'
import { MapBranding } from '../../../types/map'

const MapEditor: React.FC = () => {
  const { mapId } = useParams<{ mapId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { showError, showSuccess } = useModal()
  const mapCanvasRef = useRef<MapCanvasHandle>(null)

  const [map, setMap] = useState<Map | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTool, setActiveTool] = useState<Tool>('select')
  const [selectedElement, setSelectedElement] = useState<MapElement | null>(
    null,
  )
  const [elements, setElements] = useState<MapElement[]>([])

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [mapSettings, setMapSettings] = useState<MapSettings | null>(null)
  const [showSettingsPanel, setShowSettingsPanel] = useState(false)
  const [showSharePanel, setShowSharePanel] = useState(false)
  const [showBrandingPanel, setShowBrandingPanel] = useState(false)
  const [showEmbedPanel, setShowEmbedPanel] = useState(false)
  const [mapBranding, setMapBranding] = useState<MapBranding | undefined>(
    undefined,
  )
  const [hoveredElementId, setHoveredElementId] = useState<string | null>(null)
  const [isolatedElementId, setIsolatedElementId] = useState<string | null>(
    null,
  )
  const [showNavigator, setShowNavigator] = useState(true)
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(() => {
    return localStorage.getItem('leftPanelCollapsed') === 'true'
  })
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(() => {
    return localStorage.getItem('rightPanelCollapsed') === 'true'
  })

  useEffect(() => {
    const loadMap = async () => {
      if (!mapId) {
        navigate('/')
        return
      }

      // Load map from Firebase API
      try {
        const foundMap = await loadMapFromAPI(mapId)

        if (foundMap) {
          setMap(foundMap)
          setElements(foundMap.data.elements || [])
          // Ensure allowDragging is set to true by default if not present
          setMapSettings({
            ...foundMap.data.settings,
            allowDragging: foundMap.data.settings.allowDragging ?? true,
          })
          setMapBranding(foundMap.branding)
        } else {
          // Map not found, redirect to dashboard
          console.error('Map not found:', mapId)
          navigate('/')
          return
        }
      } catch (error) {
        console.error('Error loading map:', error)
        navigate('/')
        return
      }

      setLoading(false)
    }

    loadMap()
  }, [mapId, navigate])

  const handleSave = async () => {
    if (!map) return

    try {
      // Capture screenshot of the canvas
      const screenshot = mapCanvasRef.current?.captureScreenshot()

      const updatedMap = {
        ...map,
        data: {
          ...map.data,
          elements: elements,
          settings: mapSettings || map.data.settings,
        },
        branding: mapBranding,
        imageUrl: screenshot || map.imageUrl, // Use screenshot if available
        updatedAt: new Date(),
      }

      // Save to Firebase API
      const savedMap = await saveMapToAPI(updatedMap)

      setMap(savedMap)
      setHasUnsavedChanges(false)
      showSuccess('Map saved successfully!')
    } catch (error) {
      console.error('Error saving map:', error)
      showError('Error saving map. Please try again.')
    }
  }

  const handleShare = async () => {
    if (!map || map.ownerId !== user?.uid) return
    setShowSharePanel(true)
    // Close other panels
    setShowSettingsPanel(false)
    setShowBrandingPanel(false)
  }

  const handleBranding = () => {
    if (!map || map.ownerId !== user?.uid) return
    setShowBrandingPanel(true)
    // Close other panels
    setShowSettingsPanel(false)
    setShowSharePanel(false)
  }

  const handleBrandingSave = (branding: MapBranding) => {
    setMapBranding(branding)
    setHasUnsavedChanges(true)
  }

  const handleBack = () => {
    if (hasUnsavedChanges) {
      if (
        window.confirm(
          'You have unsaved changes. Are you sure you want to leave?',
        )
      ) {
        navigate('/')
      }
    } else {
      navigate('/')
    }
  }

  const handleAddElement = (element: MapElement) => {
    setElements(prev => [...prev, element])
    setHasUnsavedChanges(true)
    // Auto-select the newly created element
    setSelectedElement(element)
  }

  const handleUpdateElement = (
    elementId: string,
    updates: Partial<MapElement>
  ) => {
    setElements(prev =>
      prev.map(el => (el.id === elementId ? { ...el, ...updates } : el))
    )
    setHasUnsavedChanges(true)

    // Update selected element if it's the one being updated
    if (selectedElement && selectedElement.id === elementId) {
      setSelectedElement(prev => (prev ? { ...prev, ...updates } : null))
    }
  }

  const handleDeleteElement = (elementId: string) => {
    setElements(prev => prev.filter(el => el.id !== elementId))
    setHasUnsavedChanges(true)

    // Clear selection if deleted element was selected
    if (selectedElement && selectedElement.id === elementId) {
      setSelectedElement(null)
    }
  }

  const handleSettingsChange = (updates: Partial<MapSettings>) => {
    if (!mapSettings) return

    const newSettings = { ...mapSettings, ...updates }
    setMapSettings(newSettings)
    setHasUnsavedChanges(true)

    // If scale changed, recalculate all element measurements
    if (updates.scale && updates.scale !== mapSettings.scale) {
      const scaleRatio = mapSettings.scale / updates.scale
      const updatedElements = elements.map(element => {
        // Only update elements that have measurements
        if (!element.measurements) return element

        // Recalculate real measurements based on new scale
        const updatedMeasurements = { ...element.measurements }

        if (updatedMeasurements.realWidth !== undefined) {
          updatedMeasurements.realWidth =
            updatedMeasurements.realWidth * scaleRatio
        }
        if (updatedMeasurements.realHeight !== undefined) {
          updatedMeasurements.realHeight =
            updatedMeasurements.realHeight * scaleRatio
        }
        if (updatedMeasurements.realLength !== undefined) {
          updatedMeasurements.realLength =
            updatedMeasurements.realLength * scaleRatio
        }
        if (updatedMeasurements.realArea !== undefined) {
          updatedMeasurements.realArea =
            updatedMeasurements.realArea * scaleRatio * scaleRatio
        }

        return {
          ...element,
          measurements: updatedMeasurements,
        }
      })
      setElements(updatedElements)
    }

    // Update the map object as well
    if (map) {
      setMap({
        ...map,
        data: {
          ...map.data,
          settings: newSettings,
        },
      })
    }
  }

  const toggleLeftPanel = () => {
    const newState = !leftPanelCollapsed
    setLeftPanelCollapsed(newState)
    localStorage.setItem('leftPanelCollapsed', newState.toString())
  }

  const toggleRightPanel = () => {
    const newState = !rightPanelCollapsed
    setRightPanelCollapsed(newState)
    localStorage.setItem('rightPanelCollapsed', newState.toString())
  }

  if (loading) {
    return <LoadingContainer>Loading map editor...</LoadingContainer>
  }

  if (!map || !mapSettings) {
    return <LoadingContainer>Map not found</LoadingContainer>
  }

  const isOwner = map.ownerId === user?.uid

  return (
    <EditorContainer>
      <TopToolbar
        mapName={map.name}
        hasUnsavedChanges={hasUnsavedChanges}
        isOwner={isOwner}
        onSave={handleSave}
        onShare={handleShare}
        onBranding={handleBranding}
        onEmbed={() => setShowEmbedPanel(true)}
        onBack={handleBack}
        onEditSettings={() => {
          setShowSettingsPanel(!showSettingsPanel)
          // Close other panels if open
          if (!showSettingsPanel) {
            setShowSharePanel(false)
            setShowBrandingPanel(false)
            setShowEmbedPanel(false)
          }
        }}
        showingSettings={showSettingsPanel}
      />

      <MobileNotice>
        📱 Mobile View: Map editing is not available on mobile. Use the settings
        below to manage sharing, branding, and map properties.
      </MobileNotice>

      <MainLayout>
        <LeftPanel $collapsed={leftPanelCollapsed}>
          <PanelContent $collapsed={leftPanelCollapsed}>
            <PanelSwitcher>
              <SwitchButton
                $active={!showNavigator}
                onClick={() => setShowNavigator(false)}>
                🔧 Tools
              </SwitchButton>
              <SwitchButton
                $active={showNavigator}
                onClick={() => setShowNavigator(true)}>
                📋 Navigator
              </SwitchButton>
            </PanelSwitcher>
            <PanelContentArea>
              {showNavigator ? (
                <NavigatorPanel
                  elements={elements}
                  selectedElement={selectedElement}
                  hoveredElementId={hoveredElementId}
                  isolatedElementId={isolatedElementId}
                  onSelectElement={setSelectedElement}
                  onHoverElement={setHoveredElementId}
                  onToggleVisibility={elementId => {
                    handleUpdateElement(elementId, {
                      visible: !elements.find(el => el.id === elementId)
                        ?.visible,
                    })
                  }}
                  onToggleIsolation={elementId => {
                    setIsolatedElementId(
                      isolatedElementId === elementId ? null : elementId,
                    )
                  }}
                />
              ) : (
                <ToolsPanel
                  activeTool={activeTool}
                  onToolChange={setActiveTool}
                  mapSettings={mapSettings}
                  onSettingsChange={handleSettingsChange}
                />
              )}
            </PanelContentArea>
          </PanelContent>
          <LeftToggleButton onClick={toggleLeftPanel}>
            {leftPanelCollapsed ? '▶' : '◀'}
          </LeftToggleButton>
        </LeftPanel>

        <CenterPanel>
          <MapCanvas
            ref={mapCanvasRef}
            elements={elements}
            activeTool={activeTool}
            selectedElement={selectedElement}
            hoveredElementId={hoveredElementId}
            isolatedElementId={isolatedElementId}
            onSelectElement={setSelectedElement}
            onAddElement={handleAddElement}
            onUpdateElement={handleUpdateElement}
            mapSettings={mapSettings}
          />
        </CenterPanel>

        <RightPanel $collapsed={rightPanelCollapsed}>
          <RightToggleButton onClick={toggleRightPanel}>
            {rightPanelCollapsed ? '◀' : '▶'}
          </RightToggleButton>
          <PanelContent $collapsed={rightPanelCollapsed}>
            <PropertiesPanel
              selectedElement={selectedElement}
              onUpdateElement={handleUpdateElement}
              onDeleteElement={handleDeleteElement}
              onAddElement={handleAddElement}
              mapSettings={mapSettings}
              showSettingsPanel={showSettingsPanel}
              onSettingsChange={handleSettingsChange}
            />
          </PanelContent>
        </RightPanel>
      </MainLayout>

      {isOwner && (
        <>
          <SharePanel
            mapId={map.id}
            mapName={map.name}
            isOpen={showSharePanel}
            onClose={() => setShowSharePanel(false)}
          />
          <BrandingPanel
            branding={mapBranding}
            isOpen={showBrandingPanel}
            isOwner={isOwner}
            onClose={() => setShowBrandingPanel(false)}
            onSave={handleBrandingSave}
          />
          {showEmbedPanel && (
            <EmbedPanel
              mapId={map.id}
              isPublic={map.isPublic}
              onClose={() => setShowEmbedPanel(false)}
              onMakePublic={async () => {
                // Update map to be public
                try {
                  const updatedMap = { ...map, isPublic: true }
                  await saveMapToAPI(updatedMap)
                  setMap(updatedMap)
                  showSuccess('Map is now public!')
                } catch (error) {
                  showError('Failed to make map public')
                }
              }}
            />
          )}
        </>
      )}
    </EditorContainer>
  )
}

export { MapEditor }
